import type React from "react";
import { useId } from "react";
import { Controller, useFormContext } from "react-hook-form";

import type { OfferedAnswerDto } from "@/api/types";
import { FormErrorWrapper } from "@/components/Form/FormErrorWrapper";
import { Checkbox } from "@/design-system/Checkbox";
import { Radio } from "@/design-system/Radio";
import { Text } from "@/design-system/Text";
import { twResolve } from "@/helpers/tw-resolve";
import { useTranslation } from "@/translations";

import type { CommunityPollVoteFormValues } from "./CommunityPollVoteForm";

interface CommunityPollVoteAnswersInputProps {
  offeredAnswers: OfferedAnswerDto[];
  allowMultipleAnswers: boolean;
}

export function CommunityPollVoteAnswersInput({
  offeredAnswers,
  allowMultipleAnswers,
}: CommunityPollVoteAnswersInputProps): React.ReactNode {
  const form = useFormContext<CommunityPollVoteFormValues>();
  const { t } = useTranslation();
  const radioGroupName = useId();

  return (
    <FormErrorWrapper name="votes" encircle>
      <Controller<CommunityPollVoteFormValues, "votes">
        name="votes"
        control={form.control}
        rules={{
          validate: {
            required: (value) => {
              if (allowMultipleAnswers && value.length === 0) {
                return t("components.form.error.at-least-one-selected");
              }

              if (!allowMultipleAnswers && value.length === 0) {
                return t("components.form.error.exactly-one-selected");
              }
            },
          },
        }}
        render={({ field }) => {
          return (
            <ul className="flex w-full flex-col gap-space-sm" data-testid="community-poll-vote-input">
              {offeredAnswers.map((answer) => {
                const isSelected = field.value.includes(answer.id);
                const onChange = () => {
                  if (allowMultipleAnswers) {
                    field.onChange(
                      isSelected ? field.value.filter((id) => id !== answer.id) : [...field.value, answer.id],
                    );
                  } else {
                    field.onChange([answer.id]);
                  }
                };

                return (
                  <li key={answer.id} data-testid="community-poll-vote-option">
                    <label
                      className={twResolve(
                        "relative flex cursor-pointer items-center gap-space-sm rounded-lg border border-border-secondary p-space-md transition-colors",
                        isSelected
                          ? "border-selection-border-hover bg-selection-selected-surface"
                          : "bg-background-default hover:bg-background-brand-light",
                      )}
                    >
                      {allowMultipleAnswers ? (
                        <Checkbox id={answer.id} checked={isSelected} onChange={onChange} shape="rounded" />
                      ) : (
                        <Radio name={radioGroupName} checked={isSelected} value={answer.id} onChange={onChange} />
                      )}
                      <Text variant={isSelected ? "body-emphasis" : "body"}>{answer.text}</Text>
                    </label>
                  </li>
                );
              })}
            </ul>
          );
        }}
      />
    </FormErrorWrapper>
  );
}
